import { useEffect, useState } from "react";
import { getSettings, saveSettings, listFaqs, createFaq, deleteFaq, listChatSessions, getChatMessages, deleteChatSession } from "@/lib/api";
import { Plus, Trash, ChatCircleText, Copy, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";
import ChatWidget from "@/components/ChatWidget";

export default function Chatbot() {
  const [settings, setSettings] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [newFaq, setNewFaq] = useState({ question: "", answer: "" });
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionMessages, setSessionMessages] = useState([]);
  const [tab, setTab] = useState("personality");
  const [previewOpen, setPreviewOpen] = useState(false);

  const load = async () => {
    const [s, f, sess] = await Promise.all([getSettings(), listFaqs(), listChatSessions()]);
    setSettings(s);
    setFaqs(f);
    setSessions(sess);
  };

  useEffect(() => { load(); }, []);

  const saveBot = async () => {
    try {
      await saveSettings(settings);
      toast.success("Chatbot config saved");
    } catch { toast.error("Save failed"); }
  };

  const addFaq = async () => {
    if (!newFaq.question || !newFaq.answer) {
      toast.error("Q and A required");
      return;
    }
    try {
      await createFaq(newFaq);
      setNewFaq({ question: "", answer: "" });
      toast.success("FAQ added");
      load();
    } catch { toast.error("Failed"); }
  };

  const removeFaq = async (id) => {
    await deleteFaq(id);
    load();
  };

  const openSession = async (sessionId) => {
    setSelectedSession(sessionId);
    const msgs = await getChatMessages(sessionId);
    setSessionMessages(msgs);
  };

  const removeSession = async (sessionId) => {
    if (!confirm("Delete this session?")) return;
    await deleteChatSession(sessionId);
    if (selectedSession === sessionId) {
      setSelectedSession(null);
      setSessionMessages([]);
    }
    load();
  };

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const embedSnippet = `<script src="${backendUrl}/api/widget/embed.js" defer></script>`;

  if (!settings) return <div className="p-8 text-arena-muted">Loading…</div>;

  return (
    <div className="p-8" data-testid="chatbot-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="overline mb-2">// customer support</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight">VIRAL<span className="text-arena-primary">BOT</span></h1>
          <p className="text-arena-muted mt-2 text-sm">AI support agent trained on your policies. Handles orders, shipping, returns 24/7.</p>
        </div>
        <button
          onClick={() => setPreviewOpen(true)}
          data-testid="preview-bot-btn"
          className="border border-arena-primary text-arena-primary px-4 py-2 text-xs tracking-widest uppercase font-bold hover:bg-arena-primary hover:text-black flex items-center gap-2"
        >
          <Eye size={14} weight="bold" />
          Preview Bot
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-arena-border">
        {[
          { id: "personality", label: "Personality" },
          { id: "faqs", label: "FAQ Training" },
          { id: "embed", label: "Embed Code" },
          { id: "conversations", label: `Conversations (${sessions.length})` },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={`px-4 py-2 text-xs tracking-widest uppercase border-b-2 -mb-px ${
              tab === t.id ? 'border-arena-primary text-arena-primary font-bold' : 'border-transparent text-arena-muted hover:text-arena-text'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Personality Tab */}
      {tab === "personality" && (
        <div className="border border-arena-border bg-arena-surface p-6 space-y-4" data-testid="personality-tab">
          <div>
            <label className="overline block mb-1">Bot Name</label>
            <input
              data-testid="input-bot-name"
              value={settings.chatbot_name}
              onChange={e => setSettings({ ...settings, chatbot_name: e.target.value })}
              className="w-full bg-arena-bg border border-arena-border px-3 py-2 text-sm focus:border-arena-primary outline-none"
            />
          </div>
          <div>
            <label className="overline block mb-1">Welcome Message</label>
            <input
              data-testid="input-welcome"
              value={settings.chatbot_welcome}
              onChange={e => setSettings({ ...settings, chatbot_welcome: e.target.value })}
              className="w-full bg-arena-bg border border-arena-border px-3 py-2 text-sm focus:border-arena-primary outline-none"
            />
          </div>
          <div>
            <label className="overline block mb-1">Personality & Knowledge (System Prompt)</label>
            <textarea
              data-testid="input-personality"
              value={settings.chatbot_personality}
              onChange={e => setSettings({ ...settings, chatbot_personality: e.target.value })}
              rows={10}
              className="w-full bg-arena-bg border border-arena-border px-3 py-2 text-sm focus:border-arena-primary outline-none resize-none font-mono"
            />
            <p className="text-[10px] text-arena-muted mt-1">Describe how the bot should act, policies, shipping times, return windows, etc.</p>
          </div>
          <button
            onClick={saveBot}
            data-testid="save-personality-btn"
            className="bg-arena-primary text-black px-4 py-2 text-xs tracking-widest uppercase font-bold hover:bg-arena-primaryHover"
          >
            Save Configuration
          </button>
        </div>
      )}

      {/* FAQ Tab */}
      {tab === "faqs" && (
        <div className="space-y-4" data-testid="faqs-tab">
          <div className="border border-arena-border bg-arena-surface p-5">
            <div className="overline mb-3">// add faq</div>
            <div className="space-y-3">
              <input
                data-testid="faq-question-input"
                placeholder="Customer question..."
                value={newFaq.question}
                onChange={e => setNewFaq({ ...newFaq, question: e.target.value })}
                className="w-full bg-arena-bg border border-arena-border px-3 py-2 text-sm focus:border-arena-primary outline-none"
              />
              <textarea
                data-testid="faq-answer-input"
                placeholder="Answer the bot should give..."
                value={newFaq.answer}
                onChange={e => setNewFaq({ ...newFaq, answer: e.target.value })}
                rows={3}
                className="w-full bg-arena-bg border border-arena-border px-3 py-2 text-sm focus:border-arena-primary outline-none resize-none"
              />
              <button
                onClick={addFaq}
                data-testid="add-faq-btn"
                className="bg-arena-primary text-black px-4 py-2 text-xs tracking-widest uppercase font-bold hover:bg-arena-primaryHover flex items-center gap-2"
              >
                <Plus size={12} weight="bold" /> Add FAQ
              </button>
            </div>
          </div>
          <div className="border border-arena-border bg-arena-surface divide-y divide-arena-border">
            {faqs.length === 0 && <div className="p-6 text-arena-muted text-xs text-center">No FAQs yet.</div>}
            {faqs.map(f => (
              <div key={f.id} className="p-4 flex items-start gap-3" data-testid={`faq-item-${f.id}`}>
                <div className="flex-1">
                  <div className="text-sm text-arena-primary font-bold">Q: {f.question}</div>
                  <div className="text-xs text-arena-text mt-1 whitespace-pre-wrap">A: {f.answer}</div>
                </div>
                <button onClick={() => removeFaq(f.id)} className="text-arena-danger hover:text-red-400" data-testid={`delete-faq-${f.id}`}>
                  <Trash size={14} weight="bold" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Embed Tab */}
      {tab === "embed" && (
        <div className="space-y-4" data-testid="embed-tab">
          <div className="border border-arena-border bg-arena-surface p-6">
            <div className="overline mb-2">// wordpress / html embed</div>
            <p className="text-xs text-arena-muted mb-4">
              Paste this snippet into your WordPress theme&apos;s <code className="text-arena-primary">footer.php</code> (before <code className="text-arena-primary">&lt;/body&gt;</code>), or use a code-injection plugin like <em>Insert Headers and Footers</em>.
            </p>
            <div className="bg-black border border-arena-border p-4 flex items-start gap-3">
              <pre className="flex-1 text-xs text-arena-primary overflow-x-auto whitespace-pre-wrap break-all" data-testid="embed-code">{embedSnippet}</pre>
              <button
                onClick={() => { navigator.clipboard.writeText(embedSnippet); toast.success("Copied"); }}
                data-testid="copy-embed-btn"
                className="text-arena-muted hover:text-arena-primary"
              >
                <Copy size={16} weight="bold" />
              </button>
            </div>
            <div className="mt-6">
              <div className="overline mb-2">// standalone url</div>
              <div className="bg-black border border-arena-border p-4 flex items-start gap-3">
                <pre className="flex-1 text-xs text-arena-primary overflow-x-auto break-all">{`${window.location.origin}/widget`}</pre>
                <button
                  onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/widget`); toast.success("Copied"); }}
                  className="text-arena-muted hover:text-arena-primary"
                  data-testid="copy-widget-url"
                >
                  <Copy size={16} weight="bold" />
                </button>
              </div>
              <p className="text-[10px] text-arena-muted mt-2">Share this direct chat link anywhere — link in bio, email footer, DMs.</p>
            </div>
          </div>
        </div>
      )}

      {/* Conversations Tab */}
      {tab === "conversations" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="conversations-tab">
          <div className="md:col-span-1 border border-arena-border bg-arena-surface divide-y divide-arena-border max-h-[600px] overflow-y-auto">
            {sessions.length === 0 && <div className="p-6 text-arena-muted text-xs text-center">No conversations yet.</div>}
            {sessions.map(s => (
              <div
                key={s.session_id}
                onClick={() => openSession(s.session_id)}
                className={`p-3 cursor-pointer hover:bg-arena-surfaceHover ${selectedSession === s.session_id ? 'bg-arena-surfaceHover border-l-2 border-arena-primary' : ''}`}
                data-testid={`session-${s.session_id}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs text-arena-primary truncate">{s.customer_name}</span>
                  <span className="text-[9px] text-arena-muted">{s.message_count}m</span>
                </div>
                <div className="text-[10px] text-arena-muted truncate mt-1">{s.last_message}</div>
              </div>
            ))}
          </div>
          <div className="md:col-span-2 border border-arena-border bg-arena-surface min-h-[400px]">
            {selectedSession ? (
              <div>
                <div className="flex items-center justify-between p-4 border-b border-arena-border">
                  <div className="overline">// session {selectedSession.slice(0, 8)}</div>
                  <button onClick={() => removeSession(selectedSession)} className="text-arena-danger hover:text-red-400 text-xs tracking-widest uppercase" data-testid="delete-session-btn">
                    Delete
                  </button>
                </div>
                <div className="p-4 space-y-3 max-h-[540px] overflow-y-auto">
                  {sessionMessages.map(m => (
                    <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] px-3 py-2 text-xs ${
                        m.role === 'user' ? 'bg-arena-primary text-black' : 'bg-arena-surfaceHover text-arena-text border border-arena-border'
                      }`}>
                        <div className="whitespace-pre-wrap">{m.content}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-arena-muted text-xs">
                <ChatCircleText size={32} weight="thin" className="mr-2" />
                Select a conversation
              </div>
            )}
          </div>
        </div>
      )}

      {/* Preview modal */}
      {previewOpen && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setPreviewOpen(false)}>
          <div className="w-full max-w-md h-[600px]" onClick={e => e.stopPropagation()}>
            <ChatWidget onClose={() => setPreviewOpen(false)} />
          </div>
        </div>
      )}
    </div>
  );
}
